import { useMemo } from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '~/lib/utils'

import ArrowRight from '../icon/arrow-right'
import Whatsapp from '~/assets/icons/whatsapp'
import LinkedIn from '~/assets/icons/linkedin'
import Callback from '~/assets/icons/callback'
import Form from '~/assets/icons/form'
import LiveChat from '~/assets/icons/live-chat'
import Email from '~/assets/icons/email'
import Phone from '~/assets/icons/phone'
import Messages from '~/assets/icons/messages'
import World from '~/assets/icons/world'

const buttonVariants = cva('', {
  variants: {
    colour: {
      'ink-blue':
        'text-blue-d stroke-blue-d border-blue-d/40 dark:text-white dark:stroke-white dark:border-white/60',
      'mid-blue':
        'text-blue-d stroke-blue-m border-blue-m/40 dark:text-white dark:stroke-white dark:border-white/60',
      white: 'text-white hover:text-white! stroke-white border-white/60'
    },
    fill: {
      'ink-blue': 'fill-blue-d dark:fill-white',
      'mid-blue': 'fill-blue-m dark:fill-white',
      white: 'fill-white'
    }
  },
  defaultVariants: {
    colour: 'ink-blue',
    fill: 'ink-blue'
  }
})

export type ContactLinkProps = Button & {
  data: {
    type: string
    link: string
  }
}

const ContactLink = ({ data, colour, className }: ContactLinkProps) => {
  const btnColour =
    data?.strongColour && colour === 'ink-blue' ? 'mid-blue' : colour

  const linkType = useMemo(() => {
    switch (data.type) {
      case 'phone':
        return {
          icon: (
            <Phone
              fill={btnColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'Phone'}
            />
          ),
          link: `tel:${data.link.replace(/\s+|\(.*?\)/g, '')}`
        }
      case 'email':
        return {
          icon: (
            <Email
              fill={btnColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'Email'}
            />
          ),
          link: `mailto:${data.link}`
        }
      case 'text':
        return {
          icon: (
            <Messages
              fill={btnColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'Messages'}
            />
          ),
          link: `sms:${data.link}`
        }
      case 'live-chat':
        return {
          icon: (
            <LiveChat
              fill={btnColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'LiveChat'}
            />
          ),
          link: data.link
        }
      case 'enquiry-form':
        return {
          icon: (
            <Form
              fill={btnColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'Enquiry'}
            />
          ),
          link: data.link
        }
      case 'callback':
        return {
          icon: (
            <Callback
              fill={btnColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'Callback'}
            />
          ),
          link: data.link
        }
      case 'whatsapp':
        return {
          icon: (
            <Whatsapp
              fill={btnColour || 'ink-blue'}
              secondaryfill={btnColour === 'ink-blue' ? '#1d4cf2' : ''}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'WhatsApp'}
            />
          ),
          link: `https://wa.me/${data.link.replace(/\s+|\(.*?\)/g, '')}`
        }
      case 'linkedin':
        return {
          icon: (
            <LinkedIn
              fill={btnColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'LinkedIn'}
            />
          ),
          link: data.link
        }
      case 'international':
        return {
          icon: (
            <World
              fill={btnColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'World'}
            />
          ),
          link: `tel:${data.link.replace(/\s+|\(.*?\)/g, '')}`
        }

      default:
        return {
          icon: (
            <Phone
              fill={btnColour}
              width={'1.437rem'}
              height={'1.437rem'}
              title={'Phone'}
            />
          ),
          link: data.link
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.type])

  return (
    <a
      href={linkType.link}
      {...(data?.ariaLabel !== '' ? { 'aria-label': data?.ariaLabel } : {})}
      className={cn('unstyled-link group relative flex w-fit', className)}
      {...(data?.newWindow ? { target: '_blank' } : {})}
      {...(data?.newWindow ? { rel: 'noreferrer nofollow' } : {})}
    >
      <div
        className={cn(
          buttonVariants({ colour: btnColour }),
          'gap-2xs pb-3xs flex items-center border-b-[1px] transition-all'
        )}
      >
        {data?.label}
        <div className='relative flex h-full min-h-[21px] w-[21px] shrink-0 overflow-hidden sm:min-h-[22px] sm:w-[22px] lg:min-h-[23px] lg:w-[23px]'>
          <span className='absolute top-0 right-0 flex w-[46px] flex-nowrap justify-end transition-transform duration-200 group-hover:translate-x-1/2 group-focus:translate-x-1/2'>
            <ArrowRight
              fill={btnColour || 'ink-blue'}
              width={'1.437rem'}
              height={'1.437rem'}
              title={''}
            />

            <div className='self-end [&>svg]:h-[21px] [&>svg]:w-[21px] sm:[&>svg]:h-[22px] sm:[&>svg]:w-[22px] lg:[&>svg]:h-[23px] lg:[&>svg]:w-[23px]'>
              {linkType.icon}
            </div>
          </span>
        </div>
      </div>

      <svg
        height='2'
        width='100%'
        className='absolute bottom-0 left-0 z-1 origin-left scale-x-0 transition-transform duration-200 group-hover:scale-x-100 group-focus:scale-x-100'
      >
        <rect
          x='0'
          y={1}
          width='100%'
          height='1'
          className={cn(buttonVariants({ fill: btnColour, colour: null }), '')}
          strokeWidth='2'
          strokeLinecap='square'
        />
      </svg>
    </a>
  )
}

export default ContactLink
